package com.flowyride.core.designsystem.component

import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.autofill.ContentType
import androidx.compose.ui.semantics.contentType
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation

/** What a field holds: drives the keyboard, autofill (password managers) and masking. */
enum class FlowyTextFieldKind {
    Text,
    /** Username, or "username or email" on a sign-in form. */
    Username,
    Email,
    CurrentPassword,
    NewPassword,
    /** Free multi-line text. */
    Message,
    ;

    val isPassword: Boolean get() = this == CurrentPassword || this == NewPassword
}

/**
 * Outlined text field for forms.
 *
 * On the web the text is typed in a real HTML `<input>`: the canvas-drawn Compose field never opens the
 * iPhone keyboard (mobile Safari) and is invisible to password managers.
 *
 * @param onImeAction keyboard "Done"/"Go"/"Send" key. [ImeAction.Next] moves to the next field.
 */
@Composable
expect fun FlowyTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    kind: FlowyTextFieldKind = FlowyTextFieldKind.Text,
    supportingText: String? = null,
    passwordVisible: Boolean = false,
    imeAction: ImeAction = ImeAction.Default,
    onImeAction: () -> Unit = {},
    trailingIcon: @Composable (() -> Unit)? = null,
)

/** Plain Material implementation (Android, iOS). */
@Composable
internal fun MaterialFlowyTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier,
    kind: FlowyTextFieldKind,
    supportingText: String?,
    passwordVisible: Boolean,
    imeAction: ImeAction,
    onImeAction: () -> Unit,
    trailingIcon: @Composable (() -> Unit)?,
) {
    val autofill = kind.autofillContentType()
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        supportingText = supportingText?.let { { Text(it) } },
        trailingIcon = trailingIcon,
        singleLine = kind != FlowyTextFieldKind.Message,
        minLines = if (kind == FlowyTextFieldKind.Message) 2 else 1,
        maxLines = if (kind == FlowyTextFieldKind.Message) 5 else 1,
        visualTransformation = if (kind.isPassword && !passwordVisible) PasswordVisualTransformation() else VisualTransformation.None,
        keyboardOptions = KeyboardOptions(
            keyboardType = kind.keyboardType(),
            imeAction = imeAction,
            capitalization = if (kind == FlowyTextFieldKind.Message) KeyboardCapitalization.Sentences else KeyboardCapitalization.None,
            autoCorrectEnabled = kind == FlowyTextFieldKind.Message || kind == FlowyTextFieldKind.Text,
        ),
        // Next keeps its default behaviour: move to the next field.
        keyboardActions = KeyboardActions(
            onDone = { onImeAction() },
            onGo = { onImeAction() },
            onSend = { onImeAction() },
            onSearch = { onImeAction() },
        ),
        modifier = modifier.then(if (autofill != null) Modifier.semantics { contentType = autofill } else Modifier),
    )
}

private fun FlowyTextFieldKind.keyboardType() = when (this) {
    FlowyTextFieldKind.Email -> KeyboardType.Email
    FlowyTextFieldKind.CurrentPassword, FlowyTextFieldKind.NewPassword -> KeyboardType.Password
    else -> KeyboardType.Text
}

/** Android autofill services (password managers) read it. */
private fun FlowyTextFieldKind.autofillContentType(): ContentType? = when (this) {
    FlowyTextFieldKind.Username -> ContentType.Username
    FlowyTextFieldKind.Email -> ContentType.EmailAddress
    FlowyTextFieldKind.CurrentPassword -> ContentType.Password
    FlowyTextFieldKind.NewPassword -> ContentType.NewPassword
    else -> null
}
